import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import api, { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Lock, AlertCircle } from 'lucide-react';

const LOGO_URL = 'https://static.prod-images.emergentagent.com/jobs/28fc0be7-4196-4b45-b5b2-7034b2e56bae/images/d88baf08cf9aab8cd54854b5018978ec360b4e238c0fbb7dabe14674d502f36a.png';
const LOGIN_ERROR_MESSAGES = {
  google_auth_failed: 'Google sign-in failed. Please try again.',
};

export default function LoginPage() {
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleRedirecting, setGoogleRedirecting] = useState(false);
  const redirectPath = useMemo(() => {
    const requested = new URLSearchParams(location.search).get('redirect');
    if (requested && requested.startsWith('/')) {
      return requested;
    }
    return '/dashboard';
  }, [location.search]);

  useEffect(() => {
    const errCode = new URLSearchParams(location.search).get('error');
    if (errCode && LOGIN_ERROR_MESSAGES[errCode]) {
      setError(LOGIN_ERROR_MESSAGES[errCode]);
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.force_password_reset) navigate('/dashboard');
      else if (data.role === 'admin') navigate('/admin');
      else navigate(redirectPath);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    setGoogleRedirecting(true);
    try {
      const res = await api.get('/auth/google-start', { params: { redirect: redirectPath } });
      const authUrl = res?.data?.auth_url;
      if (!authUrl) {
        setGoogleRedirecting(false);
        setError('Google sign-in is not configured. Please contact support.');
        return;
      }
      window.location.href = authUrl;
    } catch (err) {
      setGoogleRedirecting(false);
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === 'string' && detail
          ? `Google sign-in unavailable: ${detail}`
          : 'Google sign-in is not configured. Please contact support.',
      );
    }
  };

  return (
    <div className="min-h-screen flex relative" data-testid="login-page">
      <button
        type="button"
        onClick={() => navigate('/')}
        className="absolute top-4 left-4 z-20 flex items-center gap-2 bg-white/85 hover:bg-white text-[#0B3D2E] border border-[#2B2B2B]/10 rounded-md px-3 py-2 shadow-sm transition-colors"
        data-testid="auth-home-btn"
      >
        <img src={LOGO_URL} alt="Nest Geneva" className="h-8 w-auto" />
        <span className="font-heading text-sm font-medium hidden sm:inline">Nest Geneva</span>
      </button>
      {/* Left - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h1 className="font-heading text-2xl sm:text-3xl text-[#2B2B2B] tracking-tight">{t('auth.login')}</h1>
            <p className="mt-2 text-sm text-[#5a5a5a]">Welcome back to Nest Geneva</p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2" data-testid="login-error">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-medium text-[#5a5a5a] uppercase tracking-wider">{t('auth.email')}</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a5a5a]/50" />
                <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                  className="pl-10 h-11 bg-white border-[#2B2B2B]/10 focus:border-[#0B3D2E] rounded-md"
                  placeholder="you@example.com" required data-testid="login-email" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-medium text-[#5a5a5a] uppercase tracking-wider">{t('auth.password')}</Label>
                <Link to="/forgot-password" className="text-xs text-[#0B3D2E] hover:underline" data-testid="forgot-password-link">{t('auth.forgot')}</Link>
              </div>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a5a5a]/50" />
                <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)}
                  className="pl-10 h-11 bg-white border-[#2B2B2B]/10 focus:border-[#0B3D2E] rounded-md"
                  placeholder="Enter your password" required data-testid="login-password" />
              </div>
            </div>
            <Button type="submit" className="w-full h-11 bg-[#0B3D2E] hover:bg-[#0f523e] text-white font-medium" disabled={loading} data-testid="login-submit-btn">
              {loading ? 'Signing in...' : t('auth.login')}
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#2B2B2B]/10" /></div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 bg-white text-[#5a5a5a]">{t('auth.or')}</span>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full mt-4 h-11 border-[#2B2B2B]/10 text-[#2B2B2B]"
              onClick={handleGoogle}
              disabled={googleRedirecting}
              data-testid="google-login-btn"
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              {googleRedirecting ? 'Redirecting...' : t('auth.google')}
            </Button>
          </div>

          <p className="mt-6 text-center text-sm text-[#5a5a5a]">
            {t('auth.noAccount')}{' '}
            <Link to={`/register?redirect=${encodeURIComponent(redirectPath)}`} className="text-[#0B3D2E] font-medium hover:underline" data-testid="register-link">{t('auth.register')}</Link>
          </p>
        </div>
      </div>

      {/* Right - Image (hidden on mobile) */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src="https://images.unsplash.com/photo-1771504184695-c2dfceb8e08f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHw0fHxnZW5ldmElMjBza3lsaW5lJTIwbGFrZSUyMGFyY2hpdGVjdHVyYWx8ZW58MHx8fHwxNzc2MzM4NjM5fDA&ixlib=rb-4.1.0&q=85"
          alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0B3D2E]/20" />
      </div>
    </div>
  );
}
